import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Rss, NotebookPen } from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { PostCard, WriteButton, useBlogSettings, FeaturedPost } from "@/components/blog/BlogBits";

const PAGE = 12;

export default function BlogHome() {
  const { user } = useAuth();
  const { data: settings } = useBlogSettings();
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(0);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["blog-posts", category, page],
    queryFn: async () => (await api.get("/blog", { params: { category: category || undefined, skip: page * PAGE, limit: PAGE } })).data,
    keepPreviousData: true,
  });
  const posts = useMemo(() => data?.posts || [], [data]);
  const heroFeatured = posts.length > 0 && page === 0 && !category && posts[0].featured;
  const gridPosts = useMemo(() => (heroFeatured ? posts.slice(1) : posts), [posts, heroFeatured]);
  const pages = Math.max(1, Math.ceil((data?.total || 0) / PAGE));

  if (settings && !settings.enabled && user?.role !== "admin") {
    return <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="blog-disabled"><p className="font-serif text-3xl text-foreground">The blog is resting for now.</p><p className="mt-3 text-muted-foreground">Check back soon.</p></div>;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid="blog-home">
      <Seo title="Blog — Wisdom Is In a Question" description="Members share how asking questions of God, and letting Him ask us, deepens intimacy, study and faith." />
      <header className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Community blog</p>
          <h1 className="mt-3 font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">Wisdom Is In a Question</h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">Every question is an invitation. When we ask God, we're invited into deeper relationship with Him, and when we ask one another, we're invited to truly know and be known. We invite you to share what questions have opened up for you: in your faith, in your relationships, and in your walk with God.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {settings?.enabled && user?.role !== "admin" ? null : settings && !settings.enabled && <span className="rounded-full border border-amber-400/60 bg-amber-50 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-amber-700" data-testid="blog-admin-preview-badge">Hidden from visitors · admin preview</span>}
          {user ? <WriteButton /> : <Link to="/blog/write" className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-muted" data-testid="blog-signin-to-write">Sign in to write</Link>}
          {user && <Link to="/blog/mine" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted" data-testid="blog-mine-link"><NotebookPen className="h-4 w-4" /> My posts</Link>}
          <a href={`${API}/blog/feed.xml`} className="rounded-full border border-border p-2.5 text-muted-foreground hover:text-gold" title="RSS feed" data-testid="blog-rss-link"><Rss className="h-4 w-4" /></a>
        </div>
      </header>

      <nav className="mt-10 flex flex-wrap gap-2" aria-label="Categories" data-testid="blog-categories">
        <button type="button" onClick={() => { setCategory(""); setPage(0); }} className={`rounded-full px-4 py-1.5 text-sm transition-colors ${!category ? "bg-primary text-primary-foreground" : "border border-border text-foreground hover:bg-muted"}`} data-testid="blog-category-all">All</button>
        {(settings?.categories || []).map((c) => (
          <button key={c} type="button" onClick={() => { setCategory(c); setPage(0); }} className={`rounded-full px-4 py-1.5 text-sm transition-colors ${category === c ? "bg-primary text-primary-foreground" : "border border-border text-foreground hover:bg-muted"}`} data-testid={`blog-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{c}</button>
        ))}
      </nav>

      {isLoading && <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <div key={`skeleton-${i}`} className="h-80 animate-pulse rounded-2xl bg-muted" />)}</div>}
      {isError && <p className="mt-8 text-sm text-muted-foreground">The blog could not be loaded.</p>}
      {data && !posts.length && (
        <div className="mt-10 rounded-2xl border border-dashed border-border p-12 text-center" data-testid="blog-empty">
          <p className="font-serif text-2xl text-foreground">No posts here yet</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Be the first to share how a question drew you closer to God.</p>
          {user && <WriteButton className="mt-6" />}
        </div>
      )}
      {heroFeatured && <FeaturedPost post={posts[0]} />}
      {posts.length > 0 && (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
          {gridPosts.map((p) => <PostCard key={p.id} post={p} />)}
        </div>
      )}
      {pages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-3">
          <Button variant="outline" className="rounded-full" disabled={page <= 0} onClick={() => setPage(page - 1)} data-testid="blog-prev-page">Previous</Button>
          <span className="font-mono text-xs text-muted-foreground">Page {page + 1} of {pages}</span>
          <Button variant="outline" className="rounded-full" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid="blog-next-page">Next</Button>
        </div>
      )}
    </div>
  );
}
